import React, { useContext, useEffect, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import {
  CompanyInfoContext,
  CompanyInfoContextValue
} from '../context/CompanyInfoContext';
import { CompanyInfoSection } from './styled';
import CompanyBlogPreview from './CompanyBlogPreview';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

interface CompanyIntroductionStyleProps {
  showMore: boolean;
}

const CompanyIntroduction = React.forwardRef<
  React.Ref<HTMLDivElement | null> | undefined,
  { isRequest: boolean }
>(({ isRequest }, ref) => {
  const context = useContext<CompanyInfoContextValue | null>(
    CompanyInfoContext
  );
  const [isEllipsisActive, setIsEllipsisActive] = useState<boolean>(false);
  const [showMore, setShowMore] = useState<boolean>(false);
  const { dataRow, moreButton } = useStyles({ showMore });
  const partnerInfo = context?.partnerInfo;

  const detectLineClamp = (element: HTMLParagraphElement) => {
    if (element.offsetHeight < element.scrollHeight) {
      setIsEllipsisActive(true);
    } else {
      setIsEllipsisActive(false);
    }
  };

  useEffect(() => {
    const textElement = document.querySelector(
      '.introduction'
    ) as HTMLParagraphElement;
    if (!textElement) return;
    detectLineClamp(textElement);
  }, [partnerInfo]);

  useEffect(() => {
    const textElement = document.querySelector(
      '.introduction'
    ) as HTMLParagraphElement;
    if (!textElement) return;

    detectLineClamp(textElement);

    window.addEventListener('resize', () => {
      detectLineClamp(textElement);
    });

    return () => {
      window.removeEventListener('resize', () => {
        detectLineClamp(textElement);
      });
    };
  }, []);

  if (!context) return null;

  if (!partnerInfo?.companyIntroduction) return null;

  return (
    <CompanyInfoSection
      ref={ref as React.RefObject<HTMLDivElement>}
      id="companyInfo"
    >
      <div className="section-title">
        <CustomizedTypography>회사소개</CustomizedTypography>
      </div>
      {partnerInfo?.companyIntroduction && (
        <>
          <div className={dataRow}>
            {partnerInfo?.companyIntroduction ? (
              <CustomizedTypography className="row-content introduction">
                {partnerInfo?.companyIntroduction}
              </CustomizedTypography>
            ) : (
              <CustomizedTypography className="no-data">
                업데이트 예정
              </CustomizedTypography>
            )}
          </div>
          {isEllipsisActive && !showMore && (
            <CustomizedButton
              variant="text"
              color="default"
              className={moreButton}
              onClick={() => setShowMore(true)}
              endIcon={<ExpandMoreIcon />}
            >
              더보기
            </CustomizedButton>
          )}
        </>
      )}
      <CompanyBlogPreview />
    </CompanyInfoSection>
  );
});

export default CompanyIntroduction;

const useStyles = makeStyles<Theme, CompanyIntroductionStyleProps>((theme) => ({
  dataRow: {
    width: '100%',
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    '& .row-title': {
      minWidth: '88px',
      maxWidth: '88px'
    },
    '& .row-content': {
      wordBreak: 'break-all',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: ({ showMore }) => (!showMore ? '-webkit-box' : 'block'),
      '-webkit-line-clamp': ({ showMore }) => (!showMore ? 3 : 'unset'),
      '-webkit-box-orient': 'vertical',
      fontWeight: 500,
      width: '100%'
    },
    '& .introduction': {
      whiteSpace: 'pre-line'
    }
  },
  moreButton: {
    width: 'fit-content',
    transform: 'translateY(-4px)'
  }
}));
